// CB:BEGIN admin-health-cards
/**
 * AdminHealthCards — Compact horizontal health-status bar for the command center.
 *
 * 5 clickable tiles in a single row: Healthy, Waiting Homeowner, Waiting Contractor,
 * Waiting Admin, Needs Attention. Each tile shows a count, label, and color indicator.
 * Clicking a tile navigates to the projects list filtered by that health category.
 */
import React, { useEffect, useState } from 'react';
import { useApi } from '../../lib/useApi';

interface HealthSummary {
  healthy: number;
  waitingHomeowner: number;
  waitingContractor: number;
  waitingAdmin: number;
  attentionRequired: number;
}

interface AdminHealthCardsProps {
  onNavigate?: (screen: string) => void;
}

interface TileDef {
  key: keyof HealthSummary;
  label: string;
  hint: string;
  accentBg: string;
  accentText: string;
  dotColor: string;
  filter: string;
  urgent?: boolean;
}

const TILES: TileDef[] = [
  {
    key: 'healthy',
    label: 'Healthy',
    hint: 'Progressing normally',
    accentBg: '#f0fdf4',
    accentText: '#166534',
    dotColor: '#22c55e',
    filter: 'health=healthy',
  },
  {
    key: 'waitingHomeowner',
    label: 'Awaiting Homeowner',
    hint: 'Homeowner action needed',
    accentBg: '#fefce8',
    accentText: '#854d0e',
    dotColor: '#eab308',
    filter: 'health=waiting_homeowner',
  },
  {
    key: 'waitingContractor',
    label: 'Awaiting Contractor',
    hint: 'Contractor action needed',
    accentBg: '#fff7ed',
    accentText: '#9a3412',
    dotColor: '#f97316',
    filter: 'health=waiting_contractor',
  },
  {
    key: 'waitingAdmin',
    label: 'Awaiting Admin',
    hint: 'Your action required',
    accentBg: '#eff6ff',
    accentText: '#1e40af',
    dotColor: '#3b82f6',
    filter: 'health=waiting_admin',
    urgent: true,
  },
  {
    key: 'attentionRequired',
    label: 'Needs Attention',
    hint: 'Stalled or disputed',
    accentBg: '#fef2f2',
    accentText: '#991b1b',
    dotColor: '#ef4444',
    filter: 'health=attention_required',
    urgent: true,
  },
];

const AdminHealthCards: React.FC<AdminHealthCardsProps> = ({ onNavigate }) => {
  const api = useApi();
  const [health, setHealth] = useState<HealthSummary | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.get('/api/admin/health-summary');
        if (data) setHealth(data as HealthSummary);
      } catch {
        // non-critical widget — silent skip
      }
    })();
  }, []);

  if (!health) return null;

  const total = (health.healthy ?? 0) + (health.waitingHomeowner ?? 0) + (health.waitingContractor ?? 0) + (health.waitingAdmin ?? 0) + (health.attentionRequired ?? 0);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Project Health</span>
        <span style={{ fontSize: 12, color: '#9ca3af' }}>{total} active projects</span>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {TILES.map((tile) => {
          const count = health[tile.key] ?? 0;
          const isEmpty = count === 0;

          return (
            <button
              key={tile.key}
              type="button"
              onClick={() => onNavigate?.(`admin-projects-list?${tile.filter}`)}
              title={tile.hint}
              style={{
                flex: '1 1 140px',
                background: isEmpty ? '#f9fafb' : tile.accentBg,
                border: `1px solid ${isEmpty ? '#e5e7eb' : tile.dotColor + '40'}`,
                borderRadius: 8,
                padding: '12px 14px',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'box-shadow 0.12s',
                outline: 'none',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none'; }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                {/* Dot indicator */}
                <span style={{
                  width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                  background: isEmpty ? '#d1d5db' : tile.dotColor,
                  display: 'inline-block',
                }} />
                <span style={{
                  fontSize: 22, fontWeight: 700,
                  color: isEmpty ? '#9ca3af' : tile.accentText,
                  lineHeight: 1,
                }}>
                  {count}
                </span>
                {tile.urgent && count > 0 && (
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: '1px 5px',
                    background: tile.dotColor, color: '#fff',
                    borderRadius: 3, letterSpacing: '0.03em',
                  }}>
                    ACTION
                  </span>
                )}
              </div>
              <div style={{ fontSize: 12, fontWeight: 600, color: isEmpty ? '#9ca3af' : tile.accentText }}>
                {tile.label}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default AdminHealthCards;
// CB:END admin-health-cards
